/**
 * The revenue equation behind the Leads page's "Revenue math" panel:
 *
 *   yearly revenue = leads/day × 365 × conversion rate × average contract
 *
 * solved in both directions — what the current pace produces, and how many
 * leads a day a revenue target demands. Pure, so the arithmetic is tested
 * without rendering.
 */

export interface RevenueAssumptions {
  /** Lead → signed-contract conversion, as a fraction (0.10 = 10%). */
  readonly conversionRate: number;
  /** Average signed contract value in dollars. */
  readonly averageContract: number;
}

/** Revenue a lead pace produces in a year under the assumptions. */
export function yearlyRevenueAtPace(leadsPerDay: number, assumptions: RevenueAssumptions): number {
  return leadsPerDay * 365 * assumptions.conversionRate * assumptions.averageContract;
}

/** What one lead is worth under the assumptions. */
export function valuePerLead(assumptions: RevenueAssumptions): number {
  return assumptions.conversionRate * assumptions.averageContract;
}

/**
 * Leads per day a yearly revenue target demands. Null when the assumptions
 * produce nothing (no conversion or worthless contracts) — no pace reaches
 * the target.
 */
export function leadsPerDayForTarget(
  targetYearlyRevenue: number,
  assumptions: RevenueAssumptions,
): number | null {
  const perLead = valuePerLead(assumptions);
  if (perLead <= 0) return null;
  return targetYearlyRevenue / (365 * perLead);
}
